import * as React from 'react';
import { cn } from '@/lib/utils';

export interface CDCurrencyProps {
  /** The numeric value to display */
  value: number | string | null | undefined;
  /** Whether to show the dollar sign */
  showDollarSign?: boolean;
  /** Whether negative values should be shown in parentheses */
  useParentheses?: boolean;
  /** Size variant */
  size?: 'default' | 'large';
  /** Whether this is an editable field */
  editable?: boolean;
  /** onChange handler for editable mode */
  onChange?: (value: number) => void;
  /** Placeholder text for empty values */
  placeholder?: string;
  /** Additional className */
  className?: string;
}

/**
 * CDCurrency - Currency display component
 *
 * Formats and displays currency values with proper alignment.
 * Supports both display-only and editable modes.
 *
 * Styling:
 * - Right-aligned
 * - Tabular numbers for proper column alignment
 * - 9pt for default, 12pt bold for large
 */
export const CDCurrency = React.forwardRef<HTMLSpanElement, CDCurrencyProps>(
  (
    {
      value,
      showDollarSign = true,
      useParentheses = false,
      size = 'default',
      editable = false,
      onChange,
      placeholder = '-',
      className,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);

    // Parse the numeric value
    const numericValue = React.useMemo(() => {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const parsed =
        typeof value === 'string' ? Number.parseFloat(value.replace(/[,$]/g, '')) : value;
      return isNaN(parsed) ? null : parsed;
    }, [value]);

    // Format the display value
    const displayValue = React.useMemo(() => {
      if (numericValue === null) {
        return placeholder;
      }

      const isNegative = numericValue < 0;
      const absValue = Math.abs(numericValue);
      const formatted = absValue.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      const withSign = showDollarSign ? `$${formatted}` : formatted;

      if (isNegative) {
        return useParentheses ? `(${withSign})` : `-${withSign}`;
      }

      return withSign;
    }, [numericValue, showDollarSign, useParentheses, placeholder]);

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      setInputValue(rawValue);
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (onChange) {
        const parsed = Number.parseFloat(inputValue.replace(/[,$]/g, ''));
        if (!isNaN(parsed)) {
          onChange(parsed);
        }
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
      setInputValue(numericValue !== null ? numericValue.toString() : '');
    };

    if (editable) {
      return (
        <input
          type="text"
          inputMode="decimal"
          value={isFocused ? inputValue : displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={cn(
            'cd-input cd-input-currency',
            size === 'large' && 'cd-currency-large',
            className,
          )}
        />
      );
    }

    return (
      <span
        ref={ref}
        className={cn('cd-currency', size === 'large' && 'cd-currency-large', className)}
      >
        {displayValue}
      </span>
    );
  },
);

CDCurrency.displayName = 'CDCurrency';

/**
 * CDPercentage - Percentage display component
 *
 * Similar to CDCurrency but for percentage values.
 */
export interface CDPercentageProps {
  /** The numeric value (e.g., 3.5 for 3.5%) */
  value: number | string | null | undefined;
  /** Number of decimal places */
  decimals?: number;
  /** Whether this is an editable field */
  editable?: boolean;
  /** onChange handler for editable mode */
  onChange?: (value: number) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Additional className */
  className?: string;
}

export const CDPercentage = React.forwardRef<HTMLSpanElement, CDPercentageProps>(
  ({ value, decimals = 3, editable = false, onChange, placeholder = '-', className }, ref) => {
    const [inputValue, setInputValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);

    const numericValue = React.useMemo(() => {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const parsed = typeof value === 'string' ? Number.parseFloat(value.replace('%', '')) : value;
      return isNaN(parsed) ? null : parsed;
    }, [value]);

    const displayValue = React.useMemo(() => {
      if (numericValue === null) {
        return placeholder;
      }
      return `${numericValue.toFixed(decimals)}%`;
    }, [numericValue, decimals, placeholder]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (onChange) {
        const parsed = Number.parseFloat(inputValue.replace('%', ''));
        if (!isNaN(parsed)) {
          onChange(parsed);
        }
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
      setInputValue(numericValue !== null ? numericValue.toString() : '');
    };

    if (editable) {
      return (
        <input
          type="text"
          inputMode="decimal"
          value={isFocused ? inputValue : displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={cn('cd-input cd-input-currency', className)}
        />
      );
    }

    return (
      <span ref={ref} className={cn('cd-currency', className)}>
        {displayValue}
      </span>
    );
  },
);

CDPercentage.displayName = 'CDPercentage';
